/**
 * Dev-only floating panel dat alle legacy `viewState[entityId]`-callsites
 * toont die de facade-helpers (vsGet/vsSet/vsKey/vsUpsertKey) hebben
 * gedetecteerd.
 *
 * Doel: snelle iteratie tijdens de key-unification cleanup (2026-05-26).
 * Elke gevonden callsite verschijnt als regel; klik op de close-knop
 * dismist 'm uit de lijst (kan na fix om duidelijk te zien wat overblijft).
 *
 * Plek: rechts-onder in canvas, schuif-uit. Verbergen via close-knop in
 * de header; togglen via Cmd/Ctrl+Shift+V (zie keymap binding hieronder).
 */
import { useEffect } from "react";
import { useDevViewStateWarnings } from "../state/viewStateAccess";
import { isDevWarningsToggleShortcut, isEditableTarget } from "./keyboardGuards";

export function DevViewStateWarningsPanel() {
  const warnings = useDevViewStateWarnings((s) => s.warnings);
  const popupVisible = useDevViewStateWarnings((s) => s.popupVisible);
  const dismiss = useDevViewStateWarnings((s) => s.dismiss);
  const dismissAll = useDevViewStateWarnings((s) => s.dismissAll);
  const togglePopup = useDevViewStateWarnings((s) => s.togglePopup);

  // Cmd/Ctrl+Shift+V toggelt de popup-zichtbaarheid. Alleen in dev, en
  // nooit in een tekstveld: daar is het "plakken als platte tekst".
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const onKey = (e: KeyboardEvent) => {
      if (!isDevWarningsToggleShortcut(e)) return;
      if (isEditableTarget(e.target)) return;
      e.preventDefault();
      togglePopup();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [togglePopup]);

  if (!import.meta.env.DEV) return null;
  if (!popupVisible) return null;
  if (warnings.length === 0) return null;

  return (
    <div
      className="ledger pointer-events-auto fixed right-4 bottom-4 z-[9999] max-h-[60vh] w-[420px] overflow-hidden rounded-lg border border-(--ld-notice-ink)/40 bg-(--ld-notice-bg) shadow-2xl"
      role="dialog"
      aria-label="Legacy viewState-callsites"
    >
      <header className="flex items-center justify-between border-transparent border-b bg-(--ld-notice-bg) px-3 py-2">
        <div className="flex items-center gap-2">
          <span aria-hidden>⚠️</span>
          <h2 className="font-semibold text-(--ld-notice-ink) text-sm">
            Legacy viewState-callsites ({warnings.length})
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={dismissAll}
            className="rounded border border-transparent bg-(--ld-surface) px-2 py-0.5 text-(--ld-notice-ink) text-xs hover:bg-(--ld-notice-bg)"
            title="Lijst leegmaken (callsites blijven loggen, alleen UI-reset)"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={togglePopup}
            className="rounded px-1 text-(--ld-notice-ink) hover:text-(--ld-notice-ink)"
            title="Verberg (Cmd/Ctrl+Shift+V om terug te halen)"
            aria-label="Verberg paneel"
          >
            ✕
          </button>
        </div>
      </header>
      <ul className="max-h-[calc(60vh-2.5rem)] overflow-y-auto divide-y divide-(--ld-rule)">
        {warnings.map((w) => (
          <li key={w.id} className="px-3 py-2 text-xs">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 font-medium text-(--ld-notice-ink)">
                  <span className="rounded bg-(--ld-notice-bg) px-1.5 py-0.5 font-mono text-xs text-(--ld-notice-ink)">
                    {w.helper}
                  </span>
                  <span className="text-(--ld-notice-ink)">×{w.hitCount}</span>
                </div>
                <pre className="mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap break-all font-mono text-xs text-(--ld-notice-ink) leading-tight">
                  {w.stack}
                </pre>
                <div className="mt-1 text-xs text-(--ld-notice-ink)">
                  bv. entityId: <code className="font-mono">{w.exampleEntityId}</code>
                </div>
              </div>
              <button
                type="button"
                onClick={() => dismiss(w.id)}
                className="shrink-0 rounded px-1 text-(--ld-notice-ink) hover:bg-(--ld-active) hover:text-(--ld-notice-ink)"
                title="Dismiss — na fix om duidelijk te houden wat overblijft"
                aria-label="Dismiss callsite"
              >
                ✓
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
